/**
 * useSEO Hook - Simplified SEO metadata management
 * 
 * This hook provides an easy-to-use interface for applying SEO metadata
 * throughout the application. It integrates with:
 * - SEOHead component for metadata rendering
 * - seo.config.ts for centralized configuration
 * - i18n system for multi-language support
 * 
 * Usage Examples:
 * 
 * 1. Using predefined metadata from seo.config.ts:
 * ```tsx
 * function HomePage() {
 *   useSEO('main', 'home');
 *   return <div>...</div>;
 * }
 * ```
 * 
 * 2. Using custom metadata:
 * ```tsx
 * function CustomPage() {
 *   useSEO({
 *     title: 'Custom Page Title',
 *     description: 'Custom description',
 *     keywords: 'keyword1, keyword2',
 *   });
 *   return <div>...</div>;
 * }
 * ```
 * 
 * 3. Article with publication date:
 * ```tsx
 * function ArticlePage() {
 *   useSEO('article', 'my-article', {
 *     publishedTime: '2024-01-15T10:00:00Z',
 *     author: 'John Doe',
 *   });
 *   return <div>...</div>;
 * }
 * ```
 * 
 * Best Practices:
 * - Always use this hook at the top level of page components
 * - Prefer predefined metadata from seo.config.ts for consistency
 * - Use custom metadata only when predefined options don't fit
 * - Keep titles under 60 characters
 * - Keep descriptions between 150-160 characters
 */

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { SEOHead, type SEOHeadProps } from '../components/SEOHead';
import { extractLanguageFromPath, DEFAULT_LANGUAGE, type SupportedLanguage } from '@config/i18n.config';
import { getSEOMetadata, type SEOMetadata } from '@config/seo.config';

/**
 * SEO hook configuration types
 */
type SEOPageType = 'main' | 'legal' | 'tool' | 'article';

/**
 * Hook overload signatures for better TypeScript support
 */
interface UseSEOWithConfig {
  (type: SEOPageType, id: string, overrides?: Partial<SEOHeadProps>): JSX.Element;
}

interface UseSEOWithCustom {
  (metadata: SEOHeadProps): JSX.Element;
}

/**
 * useSEO Hook Implementation
 * 
 * This hook can be used in two ways:
 * 1. With predefined metadata: useSEO('main', 'home')
 * 2. With custom metadata: useSEO({ title: '...', description: '...' })
 */
export function useSEO(
  typeOrMetadata: SEOPageType | SEOHeadProps,
  id?: string,
  overrides?: Partial<SEOHeadProps>
): JSX.Element {
  const location = useLocation();
  const currentLang = extractLanguageFromPath(location.pathname) || DEFAULT_LANGUAGE;

  const seoProps = useMemo(() => {
    // Case 1: Custom metadata provided directly
    if (typeof typeOrMetadata === 'object') {
      return typeOrMetadata;
    }

    // Case 2: Use predefined metadata from config
    if (typeof typeOrMetadata === 'string' && id) {
      const baseMetadata = getSEOMetadata(typeOrMetadata, id, currentLang);
      
      if (!baseMetadata) {
        // Fallback metadata if configuration not found
        console.warn(
          `SEO metadata not found for type="${typeOrMetadata}" id="${id}" lang="${currentLang}". Using fallback.`
        );
        
        return {
          title: 'Finanzly - Herramientas Financieras',
          description: 'Calculadoras y recursos financieros gratuitos para gestionar mejor tu dinero.',
          ...overrides,
        } as SEOHeadProps;
      }

      // Merge base metadata with overrides
      return {
        ...baseMetadata,
        ...overrides,
      } as SEOHeadProps;
    }

    // Fallback for invalid usage
    console.error('Invalid useSEO usage. Provide either (type, id) or custom metadata object.');
    return {
      title: 'Finanzly',
      description: 'Herramientas financieras gratuitas',
    } as SEOHeadProps;
  }, [typeOrMetadata, id, currentLang, overrides]);

  // Render SEOHead component with computed props
  return <SEOHead {...seoProps} />;
}

/**
 * Hook for dynamic metadata (e.g., user-generated content, dynamic routes)
 * 
 * Usage:
 * ```tsx
 * function DynamicPage() {
 *   const { data } = useQuery(...);
 *   
 *   useDynamicSEO({
 *     titleTemplate: (title) => `${title} | Finanzly`,
 *     title: data.title,
 *     description: data.description,
 *     ogImage: data.imageUrl,
 *   });
 *   
 *   return <div>...</div>;
 * }
 * ```
 */
export function useDynamicSEO(config: {
  title: string;
  description: string;
  titleTemplate?: (title: string) => string;
  keywords?: string;
  ogImage?: string;
  ogType?: SEOHeadProps['ogType'];
  author?: string | string[];
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
}): JSX.Element {
  const { titleTemplate, title, ...rest } = config;
  
  const finalTitle = titleTemplate ? titleTemplate(title) : title;

  return <SEOHead title={finalTitle} {...rest} />;
}

/**
 * Hook for article pages with full metadata support
 * 
 * Usage:
 * ```tsx
 * function ArticlePage() {
 *   useArticleSEO({
 *     title: 'How to Save Money',
 *     description: 'Complete guide...',
 *     author: 'Jane Smith',
 *     publishedTime: '2024-01-15T10:00:00Z',
 *     modifiedTime: '2024-01-20T15:30:00Z',
 *     section: 'Personal Finance',
 *     tags: ['savings', 'budgeting', 'finance'],
 *   });
 *   
 *   return <article>...</article>;
 * }
 * ```
 */
export function useArticleSEO(config: {
  title: string;
  description: string;
  author: string | string[];
  publishedTime: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
  keywords?: string;
  ogImage?: string;
}): JSX.Element {
  return <SEOHead ogType="article" {...config} />;
}

/**
 * Hook for tool/calculator pages
 * 
 * Usage:
 * ```tsx
 * function MortgageCalculatorPage() {
 *   useToolSEO('mortgage-calculator');
 *   return <div>...</div>;
 * }
 * ```
 */
export function useToolSEO(
  toolId: string,
  overrides?: Partial<SEOHeadProps>
): JSX.Element {
  return useSEO('tool', toolId, overrides);
}

/**
 * Hook for resource/article pages
 * 
 * Usage:
 * ```tsx
 * function ResourcePage() {
 *   useResourceSEO('compound-interest');
 *   return <div>...</div>;
 * }
 * ```
 */
export function useResourceSEO(
  resourceId: string,
  overrides?: Partial<SEOHeadProps>
): JSX.Element {
  return useSEO('article', resourceId, overrides);
}

/**
 * Default export for convenience
 */
export default useSEO;
